<script lang="ts">
  import { dismissable, type ForwardedEvent } from "#lib/internal/actions"
  import { focusTrap } from "#lib/internal/actions/focusTrap"
  import { removeScroll } from "#lib/internal/actions/removeScroll"
  import { useActions } from "#lib/internal/helpers"
  import { focus } from "#lib/internal/helpers/dom"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { tick, type Snippet } from "svelte"
  import { getDialogRootContext } from "./DialogRoot.svelte"

  type Props = BaseProps<"div"> & {
    openAutoFocus?: boolean | HTMLElement
    closeAutoFocus?: boolean
    class?: string | undefined | null
    disableClickOutside?: boolean | undefined | null
    disableEscKey?: boolean | undefined | null
    children?: Snippet
    onPointerDownOutside?: (event: ForwardedEvent<MouseEvent>["detail"]) => void
    onEscapeKeyDown?: (event: ForwardedEvent<KeyboardEvent>["detail"]) => void
  }

  let {
    openAutoFocus = $bindable(true),
    closeAutoFocus = $bindable(true),
    use = [],
    class: className = "",
    disableClickOutside = false,
    disableEscKey = false,
    children,
    onPointerDownOutside,
    onEscapeKeyDown,
    ...restProps
  }: Props = $props()

  const rootCtx = getDialogRootContext()

  $effect(() => {
    if (!$rootCtx.open && $rootCtx.triggeredId && closeAutoFocus) {
      tick().then(() => {
        focus(`#${$rootCtx.triggeredId}`)
        // eslint-disable-next-line svelte/infinite-reactive-loop
        $rootCtx.triggeredId = null
      })
    }
  })
</script>

<div
  role="dialog"
  {...restProps}
  use:focusTrap={{ disable: !$rootCtx.overlay, autofocus: openAutoFocus }}
  use:dismissable={{
    onPointerDownOutside: (event) => {
      if (disableClickOutside) {
        event.detail.preventDefault()
      } else {
        onPointerDownOutside?.(event.detail)
      }
    },
    onEscapeKeyDown: (event) => {
      if (disableEscKey) {
        event.detail.preventDefault()
      } else {
        onEscapeKeyDown?.(event.detail)
      }
    },
    onDismiss: () => {
      $rootCtx.open = false
    },
  }}
  class={cn(
    // Children keep their height so long content scrolls instead of squashing
    // overflow-hidden blocks; :where() lets a flex-1 scroll region still shrink.
    "[:where(&>*)]:shrink-0",
    "absolute-center-y shadow-bottom-100 data-[state=open]:animate-show border-general-neutral-02 bg-general-surface-01 fixed inset-x-0 z-40 mx-auto flex max-h-[90vh] w-[90vw] max-w-xl flex-col gap-3 overflow-x-hidden overflow-y-auto rounded-lg border p-6 focus-visible:ring-2 focus-visible:outline-hidden",
    className
  )}
  use:removeScroll={{ disable: !$rootCtx.overlay }}
  id={$rootCtx.contentId}
  aria-labelledby={$rootCtx.titleId}
  aria-describedby={$rootCtx.descriptionId}
  data-state={$rootCtx.open ? "open" : "closed"}
  use:useActions={use ?? []}
>
  {@render children?.()}
</div>
